// UX108 (docs/build-briefs/UX108-refresh-keeps-the-screen.md). Pure, no DOM, so a plain node:test file can import
// it (test/ui-screen-route.test.ts): the address of the screen a signed-in person is on, as a URL hash.
//
// What a route may carry, and why that is a security rule and not a style choice: opaque ids that already
// appear in API paths (an order id, a purchase order id, a count session id), enums this file lists (a
// Settings group, an operations view, a report, a range preset) and a calendar date. Never a name, a phone, an
// email, a typed amount, a PIN, a token or a card detail. parseRoute accepts only those shapes, so a hash
// someone edited by hand, or one left by a different build, parses to undefined rather than being trusted.
//
// Nothing here knows what a person may see. A route is a request to go somewhere; main.ts dispatches it
// against the live permission snapshot and lands on the parent screen when the session cannot open it.

import { ADMIN_SECTION_ORDER, type AdminSection } from "./settingsGate.ts";

/** The operations views the account menu opens (admin.ts OperationsView). Kept here as data so this module has
 * no dependency on admin.ts; test/ui-screen-route.test.ts asserts the two lists agree. */
export const ROUTE_OPERATIONS_VIEWS = [
  "reports",
  "counts",
  "stockLevels",
  "matchClients",
  "priceList",
  "startingStock",
  "transfers",
] as const;
/** UX116: the function hubs (functionHubsModel.ts HubId, same agreement test). "reports" is no longer a hub (RPTLAYOUT1-F1-S2):
 * it stays parseable so an old bookmark or link opens the Reports home instead of being dropped. */
export const ROUTE_HUB_IDS = ["reports", "inventory", "ordering", "shift"] as const;
export type RouteHubId = (typeof ROUTE_HUB_IDS)[number];

export type RouteOperationsView = (typeof ROUTE_OPERATIONS_VIEWS)[number];

/** api.ts ReportId, as data (same agreement test). */
export const ROUTE_REPORT_IDS = [
  "sales-by-day",
  "cost-margin",
  "abc-spirits",
  "sales-tax",
  "menu-profitability",
  "royalty-statement",
  "custom",
] as const;
export type RouteReportId = (typeof ROUTE_REPORT_IDS)[number];

/**
 * RPTLAYOUT1-F1-S2 (owner ruling 2026-10-19, "retire duplicates"): the old location report views that a canned report now covers.
 * An old address or link naming one still parses and opens the Reports home on the matching new report, so nothing breaks.
 * The value is the canned report id (cannedReports.ts) the home opens.
 */
export const RETIRED_REPORT_REDIRECTS: Readonly<Record<string, string>> = {
  "sales-by-item": "product-mix",
  "tips-by-employee": "tips-by-employee-day",
  "stock-value": "current-inventory-value",
  "receipts-spend": "received-spend-by-vendor",
  "count-variance": "closed-count-comparison",
};

/**
 * RPTCARDS1 (owner ruling 2026-10-20, DEC-481): the old Usage and waste view is retired and no canned report
 * replaces them, so an old address naming one opens the Reports home itself; the Royalty view is kept (DEC-477).
 */
export const RETIRED_REPORT_HOME_IDS: readonly string[] = ["usage-waste"];

const CANNED_ID = /^[a-z0-9][a-z0-9-]{0,79}$/;

/** reportFormat.ts RangePreset, as data (same agreement test). */
export const ROUTE_RANGE_PRESETS = [
  "today",
  "yesterday",
  "last7",
  "last7days",
  "last30",
  "last90",
  "thisWeek",
  "lastWeek",
  "thisMonth",
  "lastMonth",
  "custom",
] as const;
export type RouteRangePreset = (typeof ROUTE_RANGE_PRESETS)[number];

export interface ReportRange {
  readonly preset: RouteRangePreset;
  /** Present only for a custom range. */
  readonly from?: string;
  readonly to?: string;
}

export type Route =
  | { readonly kind: "tabs" }
  | { readonly kind: "ticket"; readonly orderId: string }
  | { readonly kind: "profile" }
  | { readonly kind: "settings"; readonly section: AdminSection }
  | {
      readonly kind: "view";
      readonly view: RouteOperationsView;
      readonly report?: RouteReportId;
      /** A canned report the Reports home opens on load (a retired old report's replacement). */
      readonly open?: string;
      readonly range?: ReportRange;
      readonly countSessionId?: string;
    }
  | { readonly kind: "closeout"; readonly date?: string }
  | { readonly kind: "void-history"; readonly date?: string }
  | { readonly kind: "orders"; readonly orderId?: string; readonly receive?: boolean }
  | { readonly kind: "cart" }
  | { readonly kind: "receive-stock" }
  | { readonly kind: "hub"; readonly hub: RouteHubId };

/** An opaque id: what the server mints (a UUID, or a prefixed one). Nothing a person types, so no space, no
 * punctuation beyond - and _, and a length cap. */
const OPAQUE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isOpaqueId(value: string): boolean {
  return OPAQUE_ID.test(value);
}

function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isMember<T extends string>(list: readonly T[], value: string | undefined): value is T {
  return value !== undefined && (list as readonly string[]).includes(value);
}

/** "tabs", "ticket/<id>", ... with an optional ?query, from a location hash. Undefined for anything this module
 * does not produce. */
export function parseRoute(hash: string): Route | undefined {
  if (!hash.startsWith("#/")) return undefined;
  const body = hash.slice(2);
  const queryAt = body.indexOf("?");
  const path = queryAt === -1 ? body : body.slice(0, queryAt);
  const query = new URLSearchParams(queryAt === -1 ? "" : body.slice(queryAt + 1));
  const parts = path.split("/");
  const head = parts[0];
  const a = parts[1];
  const b = parts[2];
  const count = parts.length;

  switch (head) {
    case "tabs":
      return count === 1 ? { kind: "tabs" } : undefined;
    case "profile":
      return count === 1 ? { kind: "profile" } : undefined;
    case "cart":
      return count === 1 ? { kind: "cart" } : undefined;
    case "receive-stock":
      return count === 1 ? { kind: "receive-stock" } : undefined;
    case "hub":
      return count === 2 && isMember(ROUTE_HUB_IDS, a) ? { kind: "hub", hub: a } : undefined;
    case "ticket":
      return count === 2 && a !== undefined && isOpaqueId(a)
        ? { kind: "ticket", orderId: a }
        : undefined;
    case "settings":
      return count === 2 && (a === "menu" || isMember(ADMIN_SECTION_ORDER, a))
        ? { kind: "settings", section: a }
        : undefined;
    case "closeout":
    case "void-history": {
      if (count !== 1) return undefined;
      const date = query.get("date");
      if (date !== null && !isIsoDate(date)) return undefined;
      return date === null ? { kind: head } : { kind: head, date };
    }
    case "orders": {
      if (count === 1) return { kind: "orders" };
      if (a === undefined || !isOpaqueId(a)) return undefined;
      if (count === 2) return { kind: "orders", orderId: a };
      if (count === 3 && b === "receive") return { kind: "orders", orderId: a, receive: true };
      return undefined;
    }
    case "view": {
      // RPTLAYOUT1-F1-S2: the Audit log is a Settings group now; an old address opens it there.
      if (a === "changeHistory" && count === 2) return { kind: "settings", section: "auditLog" };
      if (!isMember(ROUTE_OPERATIONS_VIEWS, a)) return undefined;
      if (a === "reports") {
        if (count === 2) {
          const open = query.get("open");
          if (open === null) return { kind: "view", view: "reports" };
          return CANNED_ID.test(open) ? { kind: "view", view: "reports", open } : undefined;
        }
        if (count === 3 && b !== undefined && Object.hasOwn(RETIRED_REPORT_REDIRECTS, b)) {
          return { kind: "view", view: "reports", open: RETIRED_REPORT_REDIRECTS[b]! };
        }
        if (count === 3 && b !== undefined && RETIRED_REPORT_HOME_IDS.includes(b)) return { kind: "view", view: "reports" };
        if (count !== 3 || !isMember(ROUTE_REPORT_IDS, b)) return undefined;
        const range = parseRange(query);
        if (range === null) return undefined;
        return range === undefined
          ? { kind: "view", view: "reports", report: b }
          : { kind: "view", view: "reports", report: b, range };
      }
      if (a === "counts") {
        if (count === 2) return { kind: "view", view: "counts" };
        if (count === 3 && b !== undefined && isOpaqueId(b)) {
          return { kind: "view", view: "counts", countSessionId: b };
        }
        return undefined;
      }
      return count === 2 ? { kind: "view", view: a } : undefined;
    }
    default:
      return undefined;
  }
}

/** undefined: no range in the query. null: a range that does not parse. */
function parseRange(query: URLSearchParams): ReportRange | undefined | null {
  const preset = query.get("range");
  if (preset === null) return undefined;
  if (!isMember(ROUTE_RANGE_PRESETS, preset)) return null;
  if (preset !== "custom") return { preset };
  const from = query.get("from");
  const to = query.get("to");
  if (from === null || to === null || !isIsoDate(from) || !isIsoDate(to) || from > to) return null;
  return { preset, from, to };
}

/** The hash for a route. parseRoute(formatRoute(r)) deep-equals r for every route this module can build. */
export function formatRoute(route: Route): string {
  switch (route.kind) {
    case "tabs":
    case "profile":
    case "cart":
    case "receive-stock":
      return `#/${route.kind}`;
    case "hub":
      return `#/hub/${route.hub}`;
    case "ticket":
      return `#/ticket/${route.orderId}`;
    case "settings":
      return `#/settings/${route.section}`;
    case "closeout":
    case "void-history":
      return route.date === undefined ? `#/${route.kind}` : `#/${route.kind}?date=${route.date}`;
    case "orders":
      if (route.orderId === undefined) return "#/orders";
      return route.receive === true
        ? `#/orders/${route.orderId}/receive`
        : `#/orders/${route.orderId}`;
    case "view": {
      if (route.view === "reports") {
        if (route.report === undefined) {
          return route.open === undefined ? "#/view/reports" : `#/view/reports?open=${route.open}`;
        }
        const base = `#/view/reports/${route.report}`;
        if (route.range === undefined) return base;
        const query = new URLSearchParams({ range: route.range.preset });
        if (route.range.preset === "custom" && route.range.from && route.range.to) {
          query.set("from", route.range.from);
          query.set("to", route.range.to);
        }
        return `${base}?${query.toString()}`;
      }
      if (route.view === "counts" && route.countSessionId !== undefined) {
        return `#/view/counts/${route.countSessionId}`;
      }
      return `#/view/${route.view}`;
    }
  }
}

/** Two routes that are the same screen of the same kind: moving between them is a change inside a screen
 * (replace the history entry), not a move to another screen (push one). */
export function sameScreenFamily(a: Route, b: Route): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "view" && b.kind === "view") return a.view === b.view;
  if (a.kind === "hub" && b.kind === "hub") return a.hub === b.hub;
  if (a.kind === "orders" && b.kind === "orders") {
    // The list, one order's detail and receiving it are three screens.
    return (
      (a.orderId === undefined) === (b.orderId === undefined) &&
      (a.receive === true) === (b.receive === true)
    );
  }
  return true;
}
